import { Table, TableTitleLayout, Tooltip } from "@shared/ui";
import { ColumnsType, TableProps } from "antd/es/table";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Search,
  TrendingUp,
} from "lucide-react";

import { ShareAltOutlined, UserOutlined } from "@ant-design/icons";
import {
  dynamicColumnsToShow,
  IgGlobalFilters,
  insightsiqPlatformWorkIdMap,
  YtGlobalFilters,
} from "@entities/global-discovery/constants";
import { useGetGlobalDiscoveryStore } from "@entities/global-discovery/store/useGetGlobalDiscoveryStoreV2";
import { BriefCreatorColumn } from "@features/breifCreatorColumn/BriefCreatorColumn";
import { CampaignSuggestButton } from "@pages/Global-Discovery/CampaignSuggestButton";
import { DynamicColumnType, IModifiedGlobalCreator } from "@shared/lib/types";
import { IBrief } from "@shared/lib/types/brief";
import { formatData } from "@shared/lib/utils/formatData";
import { Checkbox } from "@shared/ui/Checkbox";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import classNames from "classnames";
import type { Key } from "react";
import { isValidElement, useMemo, useState } from "react";
import styles from "./styles.module.scss";

interface Props {
  selectedRowKeys: string[];
  setSelectedRowKeys: (keys: string[]) => void;
  selectedRows: IModifiedGlobalCreator[];
  setSelectedRows: (rows: IModifiedGlobalCreator[]) => void;
  isLoading: boolean;
  setCurrentPage: (page: number) => void;
  selectedCampaign?: IBrief;
}

export function InfluencersTable({
  setCurrentPage,
  selectedRowKeys,
  setSelectedRowKeys,
  selectedRows,
  setSelectedRows,
  selectedCampaign,
}: Props) {
  const {
    platformId,
    backendFilters,
    data,
    isLoadingData,
    pageSize,
    currentPage,
    totalCount,
  } = useGetGlobalDiscoveryStore();

  const influencers = data?.data || [];
  const totalPages = Math.ceil((totalCount || 0) / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount || 0);

  const [influencerDrawerProps, setInfluencerDrawerProps] = useState<{
    open: boolean;
    creator?: IModifiedGlobalCreator;
  }>({
    open: false,
    creator: undefined,
  });

  const [highlightedRecordId, setHighlightedRecordId] = useState<string>("");

  const getCreatorId = (creator: IModifiedGlobalCreator): string => {
    return (
      creator.external_id?.toString() ||
      `${creator.work_platform?.id || ""}_${creator.platform_username || ""}`
    );
  };

  const currentPageKeys = useMemo(
    () => influencers.map((inf) => getCreatorId(inf)),
    [influencers]
  );

  const rowSelection: TableProps<IModifiedGlobalCreator>["rowSelection"] = {
    selectedRowKeys,
    columnWidth: 50,
    onChange: (
      newSelectedRowKeys: Key[],
      newSelectedRows: IModifiedGlobalCreator[]
    ) => {
      const currentPageKeySet = new Set(currentPageKeys);

      const keysFromOtherPages = selectedRowKeys.filter(
        (key) => !currentPageKeySet.has(key)
      );
      const rowsFromOtherPages = selectedRows.filter(
        (row) => !currentPageKeySet.has(getCreatorId(row))
      );

      setSelectedRowKeys([
        ...keysFromOtherPages,
        ...(newSelectedRowKeys as string[]),
      ]);
      setSelectedRows([...rowsFromOtherPages, ...newSelectedRows]);
    },
    renderCell: (_checked, record, _index, originNode) => {
      const creatorId = getCreatorId(record);
      const checkboxProps = isValidElement(originNode)
        ? (originNode.props as Record<string, unknown>)
        : {};

      return (
        <Checkbox
          {...checkboxProps}
          className={classNames({
            [styles.highlightedCheckbox]: highlightedRecordId === creatorId,
          })}
          iconSize={18}
        />
      );
    },
    columnTitle: (checkboxNode) => {
      if (isValidElement(checkboxNode)) {
        return (
          <Checkbox
            {...(checkboxNode.props as Record<string, unknown>)}
            iconSize={18}
          />
        );
      }
      return checkboxNode;
    },
  };

  const handleToggleCreator = (creatorId: string) => {
    const creator = influencers.find((inf) => getCreatorId(inf) === creatorId);
    if (!creator) return;

    const isSelected = selectedRowKeys.includes(creatorId);
    if (isSelected) {
      setSelectedRowKeys(selectedRowKeys.filter((id) => id !== creatorId));
      setSelectedRows(
        selectedRows.filter((row) => getCreatorId(row) !== creatorId)
      );
    } else {
      setSelectedRowKeys([...selectedRowKeys, creatorId]);
      setSelectedRows([...selectedRows, creator]);
    }
  };

  const platform = useMemo(() => {
    if (platformId === insightsiqPlatformWorkIdMap["instagram"])
      return "instagram";
    if (platformId === insightsiqPlatformWorkIdMap["youtube"]) return "youtube";
    return "tiktok";
  }, [platformId]);

  // Get platform filters for mapping facetName to title
  const platformFilters = useMemo(() => {
    if (!platformId) return [];
    const isInstagram = platformId === insightsiqPlatformWorkIdMap["instagram"];
    const isYoutube = platformId === insightsiqPlatformWorkIdMap["youtube"];
    if (isInstagram) return IgGlobalFilters;
    if (isYoutube) return YtGlobalFilters;
    return [];
  }, [platformId]);

  // Get filter value based on depth
  const getfiltervalue = (
    depth: number,
    data: any,
    insidenames: string[]
  ): string | number => {
    if (depth === 1) {
      if (
        typeof data[insidenames[0]] === "string" ||
        typeof data[insidenames[0]] === "number" ||
        typeof data[insidenames[0]] === "boolean"
      ) {
        return typeof data[insidenames[0]] === "boolean"
          ? data[insidenames[0]]
            ? "True"
            : "False"
          : formatData(data[insidenames[0]]);
      }

      return formatData(
        Array.isArray(data[insidenames[0]]) && data[insidenames[0]].length > 0
          ? data[insidenames[0]][0]?.percentage_value
          : data[insidenames[0]]?.percentage_value ||
              data[insidenames[0]]?.value ||
              data[insidenames[0]]?.name ||
              data[insidenames[0]]?.reel_views
      );
    }
    if (depth === 2 && Array.isArray(data[insidenames[0]])) {
      return data[insidenames[0]].join(" ,");
    }
    if (depth === 3) {
      return formatData(
        data[insidenames[0]]?.find(
          (e: any) =>
            e?.name === insidenames[1] ||
            e?.range === insidenames[1] ||
            e?.value === insidenames[1]
        )?.percentage_value
      );
    }
    return "";
  };

  // Generate dynamic columns from backendFilters.filterTags
  const dynamicColumns = useMemo(() => {
    const filterTags = backendFilters?.filterTags || {};

    // Get facet name from filter config
    const getFacetname = (facetName: string): string => {
      return (
        platformFilters.find((item) => item.facetName === facetName)?.title ||
        ""
      );
    };

    // Flatten filters to create dynamic columns
    const flattenFilters = (
      filters: Record<string, any>
    ): DynamicColumnType[] => {
      const result: DynamicColumnType[] = [];

      Object.entries(filters).forEach(([key, value]) => {
        if (
          value === null ||
          value === undefined ||
          !dynamicColumnsToShow.has(key)
        )
          return;

        const colName = getFacetname(key);
        const mappedKey = key;

        if (
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean"
        ) {
          result.push({ colName: colName, depth: 1, insideNames: [mappedKey] });
          return;
        }

        if (Array.isArray(value) && value.length > 0) {
          if (typeof value[0] === "string") {
            result.push({
              colName: colName,
              depth: 2,
              insideNames: [mappedKey],
            });
          } else {
            result.push(
              ...value.map((item) => ({
                colName: `${colName} : ${item?.name || item?.value}`,
                depth: 3,
                insideNames: [mappedKey, item?.name || item?.value],
              }))
            );
          }
          return;
        }
        result.push({
          colName: `${colName} ${value?.value ? ` : ${value.value}` : ""}`,
          depth: 1,
          insideNames: [mappedKey],
        });
      });

      return result;
    };

    const flattenedFilters = flattenFilters(filterTags);

    return flattenedFilters.map((item) => {
      return {
        title: <TextWithTooltip text={item.colName} limit={10} />,

        width: 100,
        key: `dynamic-${item.colName}-${item.insideNames.join("-")}`,
        render: (_: string, record: IModifiedGlobalCreator) => (
          <div>
            {getfiltervalue(
              item.depth,
              { ...record, ...record?.filter_match },
              item.insideNames
            )}
          </div>
        ),
      };
    });
  }, [backendFilters, platformFilters]);

  const columns: ColumnsType<IModifiedGlobalCreator> = [
    {
      title: "Creator's Profile",
      key: "creator",
      width: 150,
      render: (_: string, record: IModifiedGlobalCreator) => {
        return (
          <BriefCreatorColumn
            influencerName={record.full_name}
            username={record.platform_username}
            platform={platform}
            profilePicUrl={record.image_url}
            link={record.url}
          />
          // <div
          //   className={styles.creatorProfile}
          //   onClick={(e) => {
          //     e.stopPropagation();
          //     setInfluencerDrawerProps({ open: true, creator: record });
          //   }}
          // >
          //   <div className={styles.avatarContainer}>
          //     <Avatar
          //       src={record?.image_url}
          //       alt={record?.full_name}
          //       size={40}
          //       className={styles.avatar}
          //     />
          //     <div className={styles.platformBadge}>
          //       {getPlatformIcon(platform)}
          //     </div>
          //   </div>
          //   <div className={styles.creatorInfo}>
          //     <div className={styles.creatorName}>
          //       {record?.full_name}
          //       {/* AI Analysis Icon - placeholder, not implementing full tooltip */}
          //       <span className={styles.aiIcon}>
          //         <Sparkles size={14} />
          //       </span>
          //     </div>
          //     <div
          //       className={styles.creatorHandle}
          //       onClick={(e) => {
          //         e.stopPropagation();
          //         window.open(record?.url, "_blank");
          //       }}
          //     >
          //       {record?.platform_username}
          //     </div>
          //   </div>
          // </div>
        );
      },
    },
    {
      title: "Sys Flags",
      key: "system",
      width: 90,
      render: (_: string, record: IModifiedGlobalCreator) => (
        <div>
          {record?.flags?.isLinkedToInfluencer ? (
            <Tooltip title="Influencer has contact">
              {" "}
              <UserOutlined />
            </Tooltip>
          ) : record?.flags?.isInSocialMedia ? (
            <Tooltip title="Influencer social Media Found">
              <ShareAltOutlined />
            </Tooltip>
          ) : (
            "None"
          )}
        </div>
      ),
    },

    {
      title: `${platform === "instagram" ? "Followers" : "Subscribers"}`,
      key: "followers",
      width: 150,
      render: (_: string, record: IModifiedGlobalCreator) => (
        <div className={styles.followers}>
          {new Intl.NumberFormat("en", { notation: "compact" }).format(
            (platform === "instagram"
              ? record?.follower_count
              : record?.subscriber_count) || 0
          )}
        </div>
      ),
    },
    {
      title: `${platform === "instagram" ? "Avg. Views" : "Avg. Views"}`,
      key: "views",
      width: 150,
      render: (_: string, record: IModifiedGlobalCreator) => (
        <div className={styles.avgViews}>
          <Eye size={14} className={styles.eyeIcon} />
          {new Intl.NumberFormat("en", { notation: "compact" }).format(
            platform === "instagram"
              ? record?.filter_match?.instagram_options?.reel_views || 0
              : record?.average_views || 0
          )}
        </div>
      ),
    },
    {
      title: "Engagement",
      dataIndex: "engagement_rate",
      key: "engagement_rate",
      width: 150,
      render: (_: string, record: IModifiedGlobalCreator) => (
        <div className={styles.engagement}>
          <TrendingUp size={14} className={styles.trendingIcon} />
          <span className={styles.engagementValue}>
            {new Intl.NumberFormat("en", { notation: "compact" }).format(
              (record.engagement_rate || 0) * 100
            )}
            %
          </span>
        </div>
      ),
    },

    ...dynamicColumns,

    {
      title: "Campaign Status",
      width: 200,
      hidden: selectedCampaign ? false : true,
      render: (_: string, record: IModifiedGlobalCreator) => (
        <CampaignSuggestButton influencer={record} />
      ),
    },
  ];

  return (
    <div className={styles.tableContainer}>
      {/* Header Section */}
      {/* <div className={styles.tableHeader}>
        <h2 className={styles.resultsTitle}>
          <Users size={16} className={styles.usersIcon} />
          {totalCount ?? 0} Results Found
        </h2>
        {selectedRowKeys.length > 0 && (
          <span className={styles.selectedBadge}>
            {selectedRowKeys.length} selected
          </span>
        )}
      </div> */}

      {/* Table */}
      <div className={styles.tableWrapper}>
        <Table
          dataSource={influencers}
          columns={columns}
          loading={isLoadingData}
          rowKey={(record) => getCreatorId(record)}
          rowSelection={rowSelection}
          locale={{
            emptyText:
              influencers.length === 0 && !isLoadingData ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>
                    <Search size={32} className={styles.searchIcon} />
                  </div>
                  <h3 className={styles.emptyTitle}>No creators found</h3>
                  <p className={styles.emptyText}>
                    Try adjusting your search filters.
                  </p>
                </div>
              ) : null,
          }}
          title={() => (
            <TableTitleLayout totalText={`${totalCount ?? 0} Results Found`} />
          )}
          rowClassName={(record) => {
            const isSelected = selectedRowKeys.includes(getCreatorId(record));
            return isSelected ? styles.selectedRow : styles.row;
          }}
          onRow={(record) => ({
            onMouseEnter: (e) => {
              setHighlightedRecordId(getCreatorId(record));
            },
            onMouseLeave: (e) => {
              setHighlightedRecordId("");
            },
            onClick: (e) => {
              const target = e.target as HTMLElement;
              // Ignore clicks on interactive elements, as they have their own handlers
              if (
                target.closest(
                  'a, button, input, .ant-select, .ant-popover, .ant-dropdown, [role="button"], .ant-checkbox-wrapper'
                )
              ) {
                return;
              }
              handleToggleCreator(getCreatorId(record));
            },
          })}
          pagination={
            influencers.length > 0
              ? {
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCount,
                  onChange: (page) => setCurrentPage(page),
                  showSizeChanger: false,
                  showTotal: (total, range) => (
                    <span className={styles.paginationText}>
                      Showing {range[0]}-{range[1]} of {total} results
                    </span>
                  ),
                  itemRender: (page, type, originalElement) => {
                    if (type === "prev") {
                      return (
                        <div className={styles.paginationButton}>
                          <ChevronLeft
                            size={14}
                            className={styles.chevronIcon}
                          />
                          Previous
                        </div>
                      );
                    }
                    if (type === "next") {
                      return (
                        <div className={styles.paginationButton}>
                          Next
                          <ChevronRight
                            size={14}
                            className={styles.chevronIcon}
                          />
                        </div>
                      );
                    }
                    if (type === "page") {
                      return page === currentPage ? (
                        <span className={styles.pageText}>
                          Page {currentPage} of {totalPages}
                        </span>
                      ) : null;
                    }
                    return originalElement;
                  },
                  className: styles.tablePagination,
                }
              : false
          }
          className={styles.table}
          scroll={{ y: 500, x: 1000 }}
        />
      </div>

      {/* <SocialMediaDrawer
        open={influencerDrawerProps.open}
        onClose={() =>
          setInfluencerDrawerProps({ open: false, creator: undefined })
        }
        socialMediaRequest={{
          platform: platform,
          username: influencerDrawerProps.creator?.platform_username || "",
        }}
        creatorInfo={influencerDrawerProps.creator}
      /> */}
    </div>
  );
}
